import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  Share2, Facebook, Twitter, Linkedin, Mail, Copy, Check,
  MessageCircle, Link2, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// WhatsApp icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function ShareButtons({ 
  caseData, 
  caseId,
  variant = 'default', // 'default', 'compact', 'icon-only'
  showLabel = true,
  onShareComplete,
  className = ''
}) {
  const [copied, setCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // For social-share crawlers we must hit `/api/c/{id}` because Emergent's
  // ingress only proxies `/api/*` to the backend. Bare `/case/{id}` is
  // intercepted by the frontend nginx and serves the static SPA shell, so
  // crawlers (FB, Twitter, LinkedIn, Slack, etc.) never reach the per-case
  // SSR meta-tag route in `routes/short_share.py`. Routing through
  // `/api/c/{id}` makes the OG card show the per-case data; the route
  // 302-redirects humans back to `/case/{id}` so the UX is identical.
  // `internalUrl` is kept for in-app navigation links.
  const id = caseId || caseData?.id;
  const shareUrl = `${window.location.origin}/api/c/${id}`;
  const caseName = caseData?.name || 'Missing Person';
  const location = caseData?.last_seen_location || caseData?.state || 'unknown location';
  const age = caseData?.age ? `${caseData.age} years old` : '';
  
  // Create compelling share content
  const hashtags = '#MissingPerson #HelpFind #LinkTheMissing #BringThemHome';
  
  const shareTitle = `🚨 MISSING: ${caseName}`;
  const shareText = `Please help find ${caseName}${age ? ` (${age})` : ''}. Last seen in ${location}. Share to help bring them home! ${hashtags}`;
  const shortText = `Help find ${caseName}. Last seen: ${location}. Please share! 🙏`;

  const trackShare = async (platform) => {
    try {
      const response = await fetch(`${API}/cases/${caseId || caseData?.id}/share?platform=${platform}`, {
        method: 'POST',
      });
      if (response.ok) {
        const data = await response.json();
        onShareComplete?.(data.share_count);
      }
    } catch (e) {
      console.error('Failed to track share:', e);
    }
  };

  const handleShare = async (platform) => {
    // Track the share
    await trackShare(platform);

    // Perform the share action
    switch (platform) {
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shortText)}`,
          '_blank',
          'width=600,height=400'
        );
        break;
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(shortText)}&url=${encodeURIComponent(shareUrl)}&hashtags=MissingPerson,HelpFind`,
          '_blank',
          'width=600,height=400'
        );
        break;
      case 'linkedin':
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
          '_blank',
          'width=600,height=400'
        );
        break;
      case 'whatsapp':
        window.open(
          `https://wa.me/?text=${encodeURIComponent(`${shareTitle}\n\n${shortText}\n\n${shareUrl}`)}`,
          '_blank'
        );
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`${shareText}\n\nView case details: ${shareUrl}`)}`;
        break;
      case 'copy':
        try {
          await navigator.clipboard.writeText(`${shortText}\n\n${shareUrl}`);
          setCopied(true);
          toast.success('Link copied to clipboard!');
          setTimeout(() => setCopied(false), 2000);
        } catch {
          toast.error('Failed to copy link');
        }
        break;
      case 'native':
        if (navigator.share) {
          try {
            await navigator.share({
              title: shareTitle,
              text: shortText,
              url: shareUrl
            });
          } catch (err) {
            if (err.name !== 'AbortError') {
              console.error('Share failed:', err);
            }
          }
        }
        break;
      default:
        break;
    }

    setIsOpen(false);
  };

  const platforms = [
    { id: 'facebook', icon: Facebook, label: 'Facebook', color: 'hover:bg-blue-600 hover:text-white' },
    { id: 'twitter', icon: Twitter, label: 'Twitter/X', color: 'hover:bg-black hover:text-white' },
    { id: 'whatsapp', icon: WhatsAppIcon, label: 'WhatsApp', color: 'hover:bg-green-500 hover:text-white' },
    { id: 'linkedin', icon: Linkedin, label: 'LinkedIn', color: 'hover:bg-blue-700 hover:text-white' },
    { id: 'email', icon: Mail, label: 'Email', color: 'hover:bg-slate-600 hover:text-white' },
    { id: 'copy', icon: copied ? Check : Copy, label: copied ? 'Copied!' : 'Copy Link', color: copied ? 'bg-green-500 text-white' : 'hover:bg-slate-600 hover:text-white' },
  ];

  // Compact variant - just icons in a row
  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        {platforms.slice(0, 4).map((platform) => (
          <Button
            key={platform.id}
            variant="ghost"
            size="sm"
            className={`p-2 h-8 w-8 rounded-full ${platform.color}`}
            onClick={() => handleShare(platform.id)}
            title={platform.label}
          >
            <platform.icon className="w-4 h-4" />
          </Button>
        ))}
        {navigator.share && (
          <Button
            variant="ghost"
            size="sm"
            className="p-2 h-8 w-8 rounded-full hover:bg-safety-orange-500 hover:text-white"
            onClick={() => handleShare('native')}
            title="More options"
          >
            <Share2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    );
  }

  // Icon-only variant - single share button that opens menu
  if (variant === 'icon-only') {
    return (
      <div className={`relative ${className}`}>
        <Button
          variant="ghost"
          size="sm"
          className="p-2 h-9 w-9 rounded-full hover:bg-safety-orange-100 dark:hover:bg-safety-orange-900/30"
          onClick={() => setIsOpen(!isOpen)}
        >
          <Share2 className="w-4 h-4" />
        </Button>

        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setIsOpen(false)}
              />
              
              {/* Dropdown */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                className="absolute right-0 top-full mt-2 z-50 bg-white dark:bg-slate-800 rounded-xl shadow-xl border p-2 min-w-[180px]"
              >
                <div className="text-xs font-medium text-muted-foreground px-2 py-1 mb-1">
                  Share this case
                </div>
                {platforms.map((platform) => (
                  <button
                    key={platform.id}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${platform.color}`}
                    onClick={() => handleShare(platform.id)}
                  >
                    <platform.icon className="w-4 h-4" />
                    {platform.label}
                  </button>
                ))}
                {navigator.share && (
                  <button
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm hover:bg-safety-orange-500 hover:text-white transition-colors"
                    onClick={() => handleShare('native')}
                  >
                    <Share2 className="w-4 h-4" />
                    More options...
                  </button>
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Default variant - full buttons with labels
  return (
    <div className={`space-y-3 ${className}`}>
      {showLabel && (
        <div className="flex items-center gap-2 text-sm font-medium">
          <Share2 className="w-4 h-4" />
          Share to help find {caseName}
        </div>
      )}
      
      <div className="flex flex-wrap gap-2">
        {platforms.map((platform) => (
          <Button
            key={platform.id}
            variant="outline"
            size="sm"
            className={`gap-2 rounded-full transition-colors ${platform.color}`}
            onClick={() => handleShare(platform.id)}
          >
            <platform.icon className="w-4 h-4" />
            {platform.label}
          </Button>
        ))}
      </div>

      {navigator.share && (
        <Button
          variant="default"
          size="sm"
          className="w-full gap-2 bg-safety-orange-500 hover:bg-safety-orange-600 rounded-full"
          onClick={() => handleShare('native')}
        >
          <Share2 className="w-4 h-4" />
          Share via...
        </Button>
      )}
    </div>
  );
}

// Standalone share button for quick access
export function QuickShareButton({ caseData, caseId, className = '' }) {
  return (
    <ShareButtons
      caseData={caseData}
      caseId={caseId}
      variant="icon-only"
      className={className}
    />
  );
}

// Social share bar for cards
export function ShareBar({ caseData, caseId, className = '' }) {
  return (
    <ShareButtons
      caseData={caseData}
      caseId={caseId}
      variant="compact"
      className={className}
    />
  );
}
